import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap, CircleMarker, Tooltip as LeafletTooltip } from "react-leaflet";
import L from "leaflet";

const DEFAULT_STYLE = {
  fillColor: "#CBD5E1",
  fillOpacity: 0.4,
  color: "#94A3B8",
  weight: 0.5,
};

const HOVER_STYLE = {
  fillColor: "#94A3B8",
  fillOpacity: 0.65,
  color: "#475569",
  weight: 1.5,
};

const SELECTED_STYLE = {
  fillColor: "#059669",
  fillOpacity: 0.6,
  color: "#047857",
  weight: 2.5,
};

const STATE_DIM_STYLE = {
  fillColor: "#0F172A",
  fillOpacity: 0.05,
  color: "#CBD5E1",
  weight: 0.3,
};

// Choropleth color scale (light → dark amber/orange, easy to read on a light basemap)
export const HEAT_COLORS = [
  { fillColor: "#F1F5F9", color: "#CBD5E1" },  // bucket 0 (zero / very low)
  { fillColor: "#FEF3C7", color: "#FCD34D" },
  { fillColor: "#FDE68A", color: "#F59E0B" },
  { fillColor: "#FCD34D", color: "#D97706" },
  { fillColor: "#FBBF24", color: "#B45309" },
  { fillColor: "#F59E0B", color: "#92400E" },
  { fillColor: "#D97706", color: "#78350F" },  // top bucket
];

// Default fallback bucket thresholds (permits_per_1k metric)
const DEFAULT_THRESHOLDS = [0, 0.5, 1.5, 3, 6, 12];

function bucketIndex(value, thresholds) {
  for (let i = 0; i < thresholds.length; i++) {
    if (value <= thresholds[i]) return i;
  }
  return thresholds.length;
}

export function heatColorFor(value, thresholds = DEFAULT_THRESHOLDS) {
  return HEAT_COLORS[Math.min(bucketIndex(value, thresholds), HEAT_COLORS.length - 1)];
}

export function buildHeatThresholds(stats, metric) {
  // YoY can be negative — show a symmetric divergent-ish scale anchored at 0
  if (!stats || stats.count === 0) return DEFAULT_THRESHOLDS;
  if (metric === "yoy_change_pct") {
    return [-50, -10, 0, 10, 25, 50];
  }
  // Use percentile-based thresholds for other metrics
  return [
    stats.p50 / 4,
    stats.p50 / 2,
    stats.p50,
    stats.p90 / 2,
    stats.p90,
    stats.p99,
  ].map((v) => Number(v.toFixed(2)));
}

export function formatHeatValue(value, metric) {
  if (metric === "value_per_capita") return `$${Math.round(value).toLocaleString()}`;
  if (metric === "multi_family_share") return `${value}%`;
  if (metric === "yoy_change_pct") return `${value > 0 ? "+" : ""}${value}%`;
  return value.toFixed(2);
}

function FlyToBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid && bounds.isValid()) {
      map.flyToBounds(bounds, { padding: [40, 40], duration: 0.8, maxZoom: 9 });
    }
  }, [bounds, map]);
  return null;
}

export default function MapView({
  geojson,
  selectedFips,
  onSelect,
  stateFipsFilter,
  heatmapData,
  heatmapOn,
  heatmapMetric,
  heatThresholds,
  selectedPlace,
  ahjFipsSet,
}) {
  const geoJsonRef = useRef(null);
  const selectedRef = useRef(selectedFips);
  selectedRef.current = selectedFips;
  const thresholds = heatThresholds || DEFAULT_THRESHOLDS;

  const styleFn = (feature) => {
    const fips = feature.properties.STATE + feature.properties.COUNTY;
    if (selectedRef.current === fips) return SELECTED_STYLE;
    if (stateFipsFilter && feature.properties.STATE !== stateFipsFilter) {
      return STATE_DIM_STYLE;
    }
    if (heatmapOn && heatmapData) {
      const v = heatmapData[fips] || 0;
      const b = heatColorFor(v, thresholds);
      return { fillColor: b.fillColor, color: b.color, fillOpacity: 0.75, weight: 0.4 };
    }
    return DEFAULT_STYLE;
  };

  const onEachFeature = (feature, layer) => {
    const fips = feature.properties.STATE + feature.properties.COUNTY;
    const countyName = `${feature.properties.NAME} ${feature.properties.LSAD || "County"}`;
    let tooltipText = countyName;
    if (heatmapOn && heatmapData && heatmapData[fips] !== undefined) {
      tooltipText += ` — ${formatHeatValue(heatmapData[fips], heatmapMetric)}`;
    }
    layer.bindTooltip(tooltipText, { sticky: true, direction: "top", className: "county-tooltip" });
    layer.on({
      mouseover: (e) => {
        if (selectedRef.current !== fips) {
          e.target.setStyle(HOVER_STYLE);
        }
      },
      mouseout: (e) => {
        if (selectedRef.current !== fips) {
          e.target.setStyle(styleFn(feature));
        }
      },
      click: () => {
        onSelect && onSelect(fips, feature, layer);
      },
    });
    if (layer.getElement && layer.getElement()) {
      layer.getElement().setAttribute("data-testid", `county-polygon-${fips}`);
    }
  };

  useEffect(() => {
    if (geoJsonRef.current) {
      geoJsonRef.current.setStyle(styleFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFips, stateFipsFilter, heatmapOn, heatmapData]);

  // Compute centroids for counties that have AHJ intel — rendered as dots
  const ahjCentroids = useMemo(() => {
    if (!geojson || !ahjFipsSet || ahjFipsSet.size === 0) return [];
    const out = [];
    for (const f of geojson.features) {
      const fips = f.properties.STATE + f.properties.COUNTY;
      if (!ahjFipsSet.has(fips)) continue;
      try {
        const layer = L.geoJSON(f);
        const b = layer.getBounds();
        const c = b.getCenter();
        out.push({ fips, lat: c.lat, lon: c.lng, name: `${f.properties.NAME} ${f.properties.LSAD || "County"}` });
      } catch {
        /* skip */
      }
    }
    return out;
  }, [geojson, ahjFipsSet]);

  const flyBounds = useMemo(() => {
    if (!geojson) return null;
    let featuresToFit = null;
    if (selectedPlace) {
      // Center on place — return a tight bounds box ~0.2deg
      const { lat, lon } = selectedPlace;
      return L.latLngBounds([lat - 0.15, lon - 0.15], [lat + 0.15, lon + 0.15]);
    }
    if (selectedFips) {
      featuresToFit = geojson.features.filter(
        (f) => f.properties.STATE + f.properties.COUNTY === selectedFips
      );
    } else if (stateFipsFilter) {
      featuresToFit = geojson.features.filter((f) => f.properties.STATE === stateFipsFilter);
    }
    if (!featuresToFit || featuresToFit.length === 0) return null;
    const tempLayer = L.geoJSON({ type: "FeatureCollection", features: featuresToFit });
    return tempLayer.getBounds();
  }, [selectedFips, stateFipsFilter, geojson, selectedPlace]);

  return (
    <MapContainer
      data-testid="leaflet-map"
      center={[39.5, -98.35]}
      zoom={4}
      minZoom={3}
      maxZoom={12}
      scrollWheelZoom={true}
      zoomControl={true}
      style={{ height: "100%", width: "100%" }}
      preferCanvas={true}
    >
      <TileLayer
        attribution='&copy; <a href="https://carto.com/">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
      />
      {geojson && (
        <GeoJSON
          ref={geoJsonRef}
          data={geojson}
          style={styleFn}
          onEachFeature={onEachFeature}
        />
      )}
      {selectedPlace && (
        <CircleMarker
          center={[selectedPlace.lat, selectedPlace.lon]}
          radius={9}
          pathOptions={{ color: "#1D4ED8", fillColor: "#3B82F6", fillOpacity: 0.9, weight: 2 }}
          data-testid="place-marker"
        >
          <LeafletTooltip permanent direction="top" offset={[0, -8]} className="county-tooltip">
            {selectedPlace.full_name}
          </LeafletTooltip>
        </CircleMarker>
      )}
      {ahjCentroids.map((c) => (
        <CircleMarker
          key={`ahj-${c.fips}`}
          center={[c.lat, c.lon]}
          radius={4}
          pathOptions={{ color: "#FFFFFF", fillColor: "#6366F1", fillOpacity: 0.95, weight: 2 }}
          eventHandlers={{
            click: () => onSelect && onSelect(c.fips),
            add: (e) => {
              const el = e.target.getElement && e.target.getElement();
              if (el) el.setAttribute("data-testid", `ahj-marker-${c.fips}`);
            },
          }}
        >
          <LeafletTooltip direction="top" offset={[0, -4]} className="county-tooltip">
            AHJ intel · {c.name}
          </LeafletTooltip>
        </CircleMarker>
      ))}
      <FlyToBounds bounds={flyBounds} />
    </MapContainer>
  );
}
